import { DateTime } from 'luxon';
import { Dictionary } from "./util";

export interface ITimeRemaining {
    clock: string;
    parts: ITimeParts;
    remaining: number;
    progress: number;
}

export interface ITimeParts {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
}

export class DateTimeHelper {

    static readableDateRange(startDateObj: DateTime, endDateObj: DateTime, inclYear: boolean = true) {
        const startDate = startDateObj.setZone('local');
        const endDate = endDateObj.setZone('local');

        const sameDay = startDate.hasSame(endDate, 'day');
        const sameMonth = startDate.hasSame(endDate, 'month');
        const sameYear = startDate.hasSame(endDate, 'year');

        if (!sameYear) {
            return `${this.generateReadableDateString(startDate, true)} - ${this.generateReadableDateString(endDate, true)}`;
        } else if (!sameMonth) {
            return `${this.generateReadableDateString(startDate, inclYear)} - ${this.generateReadableDateString(endDate, inclYear)}`;
        } else if (!sameDay) {
            return `${this.generateReadableDateString(startDate, inclYear)} - ${this.generateReadableDateString(endDate, inclYear)}`;
        }
        // if on same day
        return `${this.generateReadableDateString(startDate)} - ${endDate.toFormat('h:mm a')}`;
    }

    private static generateReadableDateString(date: DateTime, inclYear: boolean = true) {
        return date.toFormat('EEE, MMM dd,' + (inclYear ? ' yyyy' : '') + ' h:mm a');
    }

    /**
     * Determines progress in % between start/end and how long left between now and the end.
     * @param start When the process was started
     * @param end When the process will end
     * @param now The current value to use for now
     */
    static getTimeRemaining(start: number, end: number, now?: number) {
        const response: ITimeRemaining = {
            clock: '0h 00m 00s',
            parts: {
                days: '0',
                hours: '0',
                minutes: '0',
                seconds: '0'
            },
            remaining: 0,
            progress: 0,
        };

        // Need an end date
        if (!end) {
            return response;
        }

        // If we didn't pass a now value
        if (!now) {
            now = new Date().valueOf();
        }

        // If we didn't pass a start then set the start to now
        if (!start) {
            start = now;
        }

        // Find out how long we have left.
        const remaining = (end - now);
        if (remaining > 0) {
            // Since we have time left, get the representation of time left
            response.clock = this.convertMillisecondsToDigitalClock(remaining);
            response.parts = this.getTimePartsFromMilliseconds(remaining);
            response.progress = ((now - start) / (end - start)) * 100;
            response.remaining = remaining;
        }
        return response;
    }

    /**
     * Takes milliseconds and returns a string representation of HH:MM:SS
     * @param ms Milliseconds (dateTime.valueOf())
     */
    static convertMillisecondsToDigitalClock(ms: number) {
        const parts = this.getTimePartsFromMilliseconds(ms);

        // If we have days, then show Days remaining otherwise show H M S
        if (+parts.days > 0) {
            return `${parts.days} Days ${parts.hours}h`;// ${minutesString}m`;
        } else {
            return (+parts.hours > 0 ? `${parts.hours}h ${parts.minutes}` : parts.minutes) + `m ${parts.seconds}s`;
        }
    }

    static formatDateForExcel(dateString: string) {
        if (dateString == null) { return ''; }
        const date = new Date(dateString);
        let month = '' + (date.getMonth() + 1),
            day = '' + date.getDate(),
            year = date.getFullYear();
        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [month, day, year].join('/');
    }

    static getTimePartsFromMilliseconds(ms: number) {
        let hours = Math.floor(ms / 3600000); // 1 Hour = 36000 Milliseconds
        const minutes = Math.floor((ms % 3600000) / 60000); // 1 Minutes = 60000 Milliseconds
        const seconds = Math.floor(((ms % 360000) % 60000) / 1000); // 1 Second = 1000 Milliseconds
        const days = Math.floor(hours / 24);
        hours = hours % 24;

        const minutesString = (minutes > 9 ? minutes : '0' + minutes).toString();
        const secondsString = (seconds > 9 ? seconds : '0' + seconds).toString();

        return {
            days: days.toString(),
            hours: hours.toString(),
            minutes: minutesString,
            seconds: secondsString
        }
    }




    static getDateObject(dateVal: any = new Date()): Date {
        // If dateVal isnt a date, make it a date
        const type = typeof dateVal;
        if (type === 'string' || type === 'number') {
            if (!dateVal.endsWith('Z')) {
                dateVal += 'Z';
            }
            dateVal = new Date(dateVal);
        }
        return dateVal;
    }

    /**
     * Returns a nicely formatted date from a passed in value (or current date)
     * @param dateVal (optional) The date to format
     */
    static formatDate(dateVal: any = new Date(), timeOnly: boolean = false, noTime: boolean = false) {


        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];


        // If dateVal isnt a date, make it a date
        const type = typeof dateVal;
        if (type === 'string' || type === 'number') {
            dateVal = new Date(dateVal);
        }

        // Get all the info
        const sMonth = monthNames[dateVal.getMonth()];
        const sDay = this.padValue(dateVal.getDate());
        const sYear = dateVal.getFullYear();
        let sHour = dateVal.getHours();
        const sMinute = this.padValue(dateVal.getMinutes());
        const sAMPM = (sHour >= 12 ? 'PM' : 'AM');

        if (sHour > 12) {
            sHour = sHour - 12;
        } else if (sHour === 0) {
            sHour = 12;
        }

        // Return it
        if (timeOnly) {
            return sHour + ':' + sMinute + ' ' + sAMPM;
        } else if (noTime) {
            return sMonth + ' ' + sDay + ', ' + sYear;
        } else {
            return sMonth + ' ' + sDay + ', ' + sYear + ' ' + sHour + ':' + sMinute + ' ' + sAMPM;
        }
    }

    static formatForFeed(date: string) {
        // Get the correct date/time
        const newsDateObj = new Date(date);
        const utcDateTime = Date.UTC(
            newsDateObj.getFullYear(),
            newsDateObj.getMonth(),
            newsDateObj.getDate(),
            newsDateObj.getHours(),
            newsDateObj.getMinutes()
        );
        const utcDateObj = new Date(utcDateTime);

        // Some constants
        const timeDiff = new Date(new Date().valueOf() - new Date(utcDateObj).valueOf()).getTime();
        const oneDay = (24 * 60 * 60 * 1000);
        const oneHour = (60 * 60 * 1000);

        // Determine how to format the date
        if (timeDiff < oneHour) {
            const minsAgo = Math.floor(timeDiff / 1000 / 60);
            if (minsAgo <= 0) {
                return `A few seconds ago`;
            } else {
                return `${minsAgo} minute${minsAgo > 1 ? 's' : ''} ago`;
            }
        } else if (timeDiff < oneDay) {
            const hoursAgo = Math.floor(timeDiff / oneHour);
            return `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
        } else if (timeDiff < oneDay * 30) {
            const daysAgo = Math.floor(timeDiff / oneDay);
            return `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
        }

        // Default return
        return this.formatDateShort(utcDateObj);
    }

    /**
     * Return the UTC string version of a passed in date (or current date)
     * @param date (optional) The value to get the UTC timestamp for
     */
    static getTimestamp(date: any = new Date()) {

        if (!date) {
            date = new Date();
        } else {
            const type = typeof date;
            if (type === 'string' || type === 'number') {
                date = new Date(date);
            }
        }

        // NEED TO USE UTC FRONT TO BACK
        return date.toUTCString();
    }

    static convertUTCDateToLocalDate(dateString: string) {
        const utcdate = new Date(dateString + 'Z');
        return new Date(utcdate);
    }

    static makeInputDate(serverDateString: string) {
        if (serverDateString == null) { return undefined; } // needs undefined or api will try to parse null and barf
        var d;
        // check if there's a timezone offset, in which case this is from a DateTimeOffset field
        if (serverDateString.includes('+') || serverDateString.lastIndexOf('-') > 10) {
            d = new Date(serverDateString);
        } else {
            // append Z to tell javascript this is a UTC data, in case server didn't
            d = new Date(serverDateString + (serverDateString.endsWith('Z') ? '' : 'Z'));
        }
        return d.getFullYear() + '-' +
            DateTimeHelper.padValue(d.getMonth() + 1) + '-' +
            DateTimeHelper.padValue(d.getDate()) + 'T' +
            DateTimeHelper.padValue(d.getHours()) + ':' +
            DateTimeHelper.padValue(d.getMinutes());
    }


    static formatDateShort(dateVal: any = new Date()) {

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
        ];

        // If dateVal isnt a date, make it a date
        const type = typeof dateVal;
        if (type === 'string' || type === 'number') {
            dateVal = new Date(dateVal);
        }

        // Get all the info
        const sMonth = monthNames[dateVal.getMonth()];
        const sDay = dateVal.getDate();
        const sYear = dateVal.getFullYear();

        // Return it
        return sMonth + ' ' + sDay + ' ' + sYear;
    }

    // https://stackoverflow.com/a/48868230/7015266
    static formatDateFrom(dateFrom: any = new Date()) {
        var fulldays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        var dt = new Date(dateFrom),
            date = dt.getDate(),
            month = months[dt.getMonth()],
            diffDays = new Date().getDate() - date,
            diffMonths = new Date().getMonth() - dt.getMonth(),
            diffYears = new Date().getFullYear() - dt.getFullYear();

        if (diffYears === 0 && diffDays === 0 && diffMonths === 0) {
            return 'Today';
        } else if (diffYears === 0 && diffDays === 1) {
            return 'Yesterday';
        } else if (diffYears === 0 && (diffDays < -1 && diffDays > -7)) {
            return fulldays[dt.getDay()];
        } else if (diffYears >= 1) {
            return month + ' ' + date + ', ' + new Date(dateFrom).getFullYear();
        } else {
            return month + ' ' + date;
        }
    }

    private static padValue(value: number) {
        return (value < 10) ? '0' + value : value;
    }


    static timezoneMappings: Dictionary<string> = {
        'America/Adak': 'Hawaiian Standard Time',
        'America/Anchorage': 'Alaskan Standard Time',
        'America/Anguilla': 'Atlantic Standard Time',
        'America/Antigua': 'Atlantic Standard Time',
        'America/Araguaina': 'Brasilia Standard Time',
        'America/Argentina/Buenos_Aires': 'Argentina Standard Time',
        'America/Argentina/Catamarca': 'Argentina Standard Time',
        'America/Argentina/Cordoba': 'Argentina Standard Time',
        'America/Argentina/Jujuy': 'Argentina Standard Time',
        'America/Argentina/La_Rioja': 'Argentina Standard Time',
        'America/Argentina/Mendoza': 'Argentina Standard Time',
        'America/Argentina/Rio_Gallegos': 'Argentina Standard Time',
        'America/Argentina/Salta': 'Argentina Standard Time',
        'America/Argentina/San_Juan': 'Argentina Standard Time',
        'America/Argentina/San_Luis': 'Argentina Standard Time',
        'America/Argentina/Tucuman': 'Argentina Standard Time',
        'America/Argentina/Ushuaia': 'Argentina Standard Time',
        'America/Aruba': 'Atlantic Standard Time',
        'America/Asuncion': 'Paraguay Standard Time',
        'America/Atikokan': 'Eastern Standard Time',
        'America/Bahia': 'Bahia Standard Time',
        'America/Bahia_Banderas': 'Central Standard Time (Mexico)',
        'America/Barbados': 'Atlantic Standard Time',
        'America/Belem': 'Brasilia Standard Time',
        'America/Belize': 'Central Standard Time (Mexico)',
        'America/Blanc-Sablon': 'Atlantic Standard Time',
        'America/Boa_Vista': 'Amazon Standard Time',
        'America/Bogota': 'Colombia Standard Time',
        'America/Boise': 'Mountain Standard Time',
        'America/Cambridge_Bay': 'Mountain Standard Time',
        'America/Campo_Grande': 'Amazon Standard Time',
        'America/Cancun': 'Eastern Standard Time',
        'America/Caracas': 'Venezuela Standard Time',
        'America/Cayenne': 'French Guiana Standard Time',
        'America/Cayman': 'Eastern Standard Time',
        'America/Chicago': 'Central Standard Time',
        'America/Chihuahua': 'Mountain Standard Time (Mexico)',
        'America/Costa_Rica': 'Central Standard Time (Mexico)',
        'America/Creston': 'Mountain Standard Time',
        'America/Cuiaba': 'Amazon Standard Time',
        'America/Curacao': 'Atlantic Standard Time',
        'America/Danmarkshavn': 'UTC',
        'America/Dawson': 'Pacific Standard Time',
        'America/Dawson_Creek': 'Mountain Standard Time',
        'America/Denver': 'Mountain Standard Time',
        'America/Detroit': 'Eastern Standard Time',
        'America/Dominica': 'Atlantic Standard Time',
        'America/Edmonton': 'Mountain Standard Time',
        'America/Eirunepe': 'Brazil Standard Time',
        'America/El_Salvador': 'Central Standard Time (Mexico)',
        'America/Fort_Nelson': 'Mountain Standard Time',
        'America/Fortaleza': 'Brasilia Standard Time',
        'America/Glace_Bay': 'Atlantic Standard Time',
        'America/Godthab': 'Greenland Standard Time',
        'America/Goose_Bay': 'Atlantic Standard Time',
        'America/Grand_Turk': 'Eastern Standard Time',
        'America/Grenada': 'Atlantic Standard Time',
        'America/Guadeloupe': 'Atlantic Standard Time',
        'America/Guatemala': 'Central Standard Time (Mexico)',
        'America/Guayaquil': 'Ecuador Standard Time',
        'America/Guyana': 'Guyana Standard Time',
        'America/Halifax': 'Atlantic Standard Time',
        'America/Havana': 'Cuba Standard Time',
        'America/Hermosillo': 'Mexican Pacific Standard Time',
        'America/Indiana/Indianapolis': 'Eastern Standard Time',
        'America/Indiana/Knox': 'Central Standard Time',
        'America/Indiana/Marengo': 'Eastern Standard Time',
        'America/Indiana/Petersburg': 'Eastern Standard Time',
        'America/Indiana/Tell_City': 'Central Standard Time',
        'America/Indiana/Vevay': 'Eastern Standard Time',
        'America/Indiana/Vincennes': 'Eastern Standard Time',
        'America/Indiana/Winamac': 'Eastern Standard Time',
        'America/Inuvik': 'Mountain Standard Time',
        'America/Iqaluit': 'Eastern Standard Time',
        'America/Jamaica': 'Eastern Standard Time',
        'America/Juneau': 'Alaskan Standard Time',
        'America/Kentucky/Louisville': 'Eastern Standard Time',
        'America/Kentucky/Monticello': 'Eastern Standard Time',
        'America/Kralendijk': 'Atlantic Standard Time',
        'America/La_Paz': 'Bolivia Standard Time',
        'America/Lima': 'Peru Standard Time',
        'America/Los_Angeles': 'Pacific Standard Time',
        'America/Lower_Princes': 'Atlantic Standard Time',
        'America/Maceio': 'Brasilia Standard Time',
        'America/Managua': 'Central Standard Time (Mexico)',
        'America/Manaus': 'Amazon Standard Time',
        'America/Marigot': 'Atlantic Standard Time',
        'America/Martinique': 'Atlantic Standard Time',
        'America/Matamoros': 'Central Standard Time',
        'America/Mazatlan': 'Mexican Pacific Standard Time',
        'America/Menominee': 'Central Standard Time',
        'America/Merida': 'Central Standard Time (Mexico)',
        'America/Metlakatla': 'Alaskan Standard Time',
        'America/Mexico_City': 'Central Standard Time (Mexico)',
        'America/Miquelon': 'Saint Pierre and Miquelon Standard Time',
        'America/Moncton': 'Atlantic Standard Time',
        'America/Monterrey': 'Central Standard Time (Mexico)',
        'America/Montevideo': 'Montevideo Standard Time',
        'America/Montreal': 'Eastern Standard Time',
        'America/Montserrat': 'Atlantic Standard Time',
        'America/Nassau': 'Eastern Standard Time',
        'America/New_York': 'Eastern Standard Time',
        'America/Nipigon': 'Eastern Standard Time',
        'America/Nome': 'Alaskan Standard Time',
        'America/Noronha': 'UTC-02',
        'America/North_Dakota/Beulah': 'Mountain Standard Time',
        'America/North_Dakota/Center': 'Central Standard Time',
        'America/North_Dakota/New_Salem': 'Mountain Standard Time',
        'America/Ojinaga': 'Mountain Standard Time',
        'America/Panama': 'Eastern Standard Time',
        'America/Pangnirtung': 'Eastern Standard Time',
        'America/Paramaribo': 'Suriname Standard Time',
        'America/Phoenix': 'Mountain Standard Time',
        'America/Port-au-Prince': 'Eastern Standard Time',
        'America/Port_of_Spain': 'Atlantic Standard Time',
        'America/Porto_Velho': 'Amazon Standard Time',
        'America/Puerto_Rico': 'Atlantic Standard Time',
        'America/Punta_Arenas': 'Magallanes Standard Time',
        'America/Rainy_River': 'Central Standard Time',
        'America/Rankin_Inlet': 'Central Standard Time',
        'America/Recife': 'Brasilia Standard Time',
        'America/Regina': 'Central Standard Time',
        'America/Resolute': 'Eastern Standard Time',
        'America/Rio_Branco': 'Brazil Standard Time',
        'America/Santa_Isabel': 'Pacific Standard Time (Mexico)',
        'America/Santarem': 'Brasilia Standard Time',
        'America/Santiago': 'Chile Standard Time',
        'America/Santo_Domingo': 'Atlantic Standard Time',
        'America/Sao_Paulo': 'Brasilia Standard Time',
        'America/Scoresbysund': 'Azores Standard Time',
        'America/Sitka': 'Alaskan Standard Time',
        'America/St_Barthelemy': 'Atlantic Standard Time',
        'America/St_Johns': 'Newfoundland Standard Time',
        'America/St_Kitts': 'Atlantic Standard Time',
        'America/St_Lucia': 'Atlantic Standard Time',
        'America/St_Thomas': 'Atlantic Standard Time',
        'America/St_Vincent': 'Atlantic Standard Time',
        'America/Swift_Current': 'Central Standard Time',
        'America/Tegucigalpa': 'Central Standard Time (Mexico)',
        'America/Thule': 'Atlantic Standard Time',
        'America/Thunder_Bay': 'Eastern Standard Time',
        'America/Tijuana': 'Pacific Standard Time',
        'America/Toronto': 'Eastern Standard Time',
        'America/Tortola': 'Atlantic Standard Time',
        'America/Vancouver': 'Pacific Standard Time',
        'America/Whitehorse': 'Pacific Standard Time',
        'America/Winnipeg': 'Central Standard Time',
        'America/Yakutat': 'Alaskan Standard Time',
        'America/Yellowknife': 'Mountain Standard Time',
    }


    static getDateKey(d: Date = new Date()) {
        return Number(d.getFullYear().toString() + DateTimeHelper.padValue(d.getMonth() + 1).toString() + DateTimeHelper.padValue(d.getDate()).toString());
    }
}
